import React, { useState } from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { LinkOff } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService, ShopifyLinkResetResult } from '../../services/api';
import { ConfirmDialog } from '../ui';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganizations } from '../../contexts/OrganizationContext';
import { canManageIntegrationCredentials } from '../../utils/integrationCredentials';

/** `mikes-camera` (the stored shop handle) reads as `mikes-camera.myshopify.com`; a full domain is kept. */
export function shopifyShopLabel(shopDomain: string | null | undefined): string | null {
  const value = String(shopDomain || '').trim();
  if (!value) return null;
  return value.includes('.') ? value : `${value}.myshopify.com`;
}

/**
 * Reset Shopify link for one product on one Shopify integration: unlinks it from its current
 * listing (nothing is deleted in Shopify) and queues a sync that matches it again by SKU / UPC.
 * A store with several Shopify integrations gets one of these per shop; the confirm dialog names
 * the shop either way. Owner/admin only, the same rule the API applies; everyone else sees nothing.
 */
export function ResetShopifyLinkButton({
  integrationId,
  productId,
  shopDomain = null,
  showShopInLabel = false,
  blockedReason = null,
  disabled = false,
}: {
  integrationId: string;
  productId: string;
  /** The integration's shop, named in the dialog (and on the button when `showShopInLabel`). */
  shopDomain?: string | null;
  /** Set when the product is linked on more than one shop, so each button says which. */
  showShopInLabel?: boolean;
  /**
   * Why no sync could match the product again on this shop (product sync off, or the product not
   * in its sync selection). The API refuses the reset then; the button is disabled and says why.
   */
  blockedReason?: 'product_sync_off' | 'not_selected' | null;
  disabled?: boolean;
}) {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const { userProfile } = useAuth();
  const { actingOrganization } = useOrganizations();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const shop = shopifyShopLabel(shopDomain);

  const canReset = canManageIntegrationCredentials(
    actingOrganization?.role,
    (userProfile as { role?: string } | null)?.role,
  );

  const mutation = useMutation({
    mutationFn: () => apiService.resetShopifyLink(integrationId, productId),
    onSuccess: (result: ShopifyLinkResetResult) => {
      setConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['erp-sync-audit', productId] });
      showToast(
        result.queuedBehindRunId
          ? t('integrations.resetShopifyLink.queuedBehind', {
              runId: result.runId,
              activeRunId: result.queuedBehindRunId,
            })
          : t('integrations.resetShopifyLink.queued', { runId: result.runId }),
        'success',
      );
    },
    onError: (err: Error) => {
      setConfirmOpen(false);
      showToast(err.message || t('integrations.resetShopifyLink.failed'), 'error');
    },
  });

  if (!canReset) return null;

  return (
    <>
      <Tooltip
        title={
          blockedReason === 'product_sync_off'
            ? t('integrations.resetShopifyLink.blockedProductSyncOff')
            : blockedReason === 'not_selected'
              ? t('integrations.resetShopifyLink.blockedNotSelected')
              : t('integrations.resetShopifyLink.tooltip')
        }
        arrow
      >
        <span>
          <Button
            size="small"
            variant="text"
            startIcon={mutation.isPending ? <CircularProgress size={14} /> : <LinkOff />}
            onClick={() => setConfirmOpen(true)}
            disabled={disabled || mutation.isPending || Boolean(blockedReason)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {showShopInLabel && shop
              ? t('integrations.resetShopifyLink.actionForShop', { shop })
              : t('integrations.resetShopifyLink.action')}
          </Button>
        </span>
      </Tooltip>
      <ConfirmDialog
        open={confirmOpen}
        title={
          shop
            ? t('integrations.resetShopifyLink.confirmTitleForShop', { shop })
            : t('integrations.resetShopifyLink.confirmTitle')
        }
        message={
          shop
            ? t('integrations.resetShopifyLink.confirmBodyForShop', { shop })
            : t('integrations.resetShopifyLink.confirmBody')
        }
        confirmLabel={t('integrations.resetShopifyLink.confirmLabel')}
        cancelLabel={t('common.cancel')}
        confirmColor="warning"
        loading={mutation.isPending}
        onConfirm={() => mutation.mutate()}
        onClose={() => {
          if (!mutation.isPending) setConfirmOpen(false);
        }}
      />
    </>
  );
}
